import { restrictions, temporaryRestrictions } from "./rules/tables";
import type { LocationCheck, LocationVerdict } from "./types";

/**
 * Turns a 2D check against the permanent-restrictions dataset into a verdict.
 * It never reports clearance: temporary restrictions are not in the dataset.
 * A minimum 30m uncertainty stops typed coordinates implying survey precision.
 */
export function assessLocation(check: LocationCheck): LocationVerdict {
  const temporary = temporaryRestrictions.reminder;
  if (check.airspaceStale) {
    return {
      status: "unknown",
      level: "danger",
      title: "No location result",
      message:
        "The permanent-restrictions dataset is missing or stale. Do not use this tool to plan a flight.",
    };
  }

  const accuracy = Math.round(check.accuracy);
  if (check.zonesInside.length > 0) {
    const names = check.zonesInside.map(({ name }) => name).join(", ");
    const permissions = new Set(check.zonesInside.map(({ type }) => zonePermission(type)));
    return {
      status: "restricted",
      level: "danger",
      title: `Inside permanent restriction: ${names}`,
      message: `Flying here is illegal without permission. ${[...permissions].join(" ")} This is a 2D check; read each zone's vertical limits. Position accuracy: ${accuracy}m.`,
    };
  }

  const uncertainty = Math.round(Math.max(check.accuracy, 30));
  if (check.nearestDistance !== null && check.nearestDistance <= uncertainty) {
    return {
      status: "boundary",
      level: "warning",
      title: "Near a restriction boundary",
      message: `Nearest mapped edge is about ${Math.round(check.nearestDistance)}m away, within the ${uncertainty}m uncertainty margin. Treat the result as uncertain. ${temporary}`,
    };
  }

  const distance =
    check.nearestDistance === null ? "not available" : `${Math.round(check.nearestDistance)}m`;
  return {
    status: "clear",
    level: "info",
    title: `No permanent restrictions found in dataset effective ${check.effectiveDate ?? "unknown"}. This is not clearance.`,
    message: `Nearest mapped edge: ${distance}. ${temporary} Local rules and landowner permission still apply.`,
  };
}

/** Who grants permission for a permanent zone, from its type or designator. */
export function zonePermission(zoneType: string): string {
  const row = restrictions.find(({ matches }) => matches(zoneType));
  if (!row) throw new Error("Restrictions table has no row for this zone.");
  return row.permission;
}
